import {
  CreateExportTaskRequestDtoCollapseVariantsByProductIdEnum,
  ProductServiceType,
  TransformersEnum,
  TranslateLanguage,
} from "definitions/exporter";
import { SourceCode } from "src/constants/sources";

const EXPORTABLE_BY_SOURCE: Record<ProductServiceType, boolean> = {
  [ProductServiceType.Poizon]: true,
  [ProductServiceType.Dewu]: true,
  [ProductServiceType.Goat]: true,
  [ProductServiceType.Shihuo]: false,
};

export const EXPORT_SOURCES: SourceCode[] = Object.values(
  ProductServiceType,
).filter((source) => EXPORTABLE_BY_SOURCE[source]);

export type TransformerGroupId =
  | "translation"
  | "mapping"
  | "cleanup"
  | "images"
  | "normalization"
  | "generation";

export interface TransformerGroup {
  id: TransformerGroupId;
  title: string;
  transformers: TransformersEnum[];
}

export const TRANSFORMER_GROUPS: TransformerGroup[] = [
  {
    id: "translation",
    title: "Перевод",
    transformers: [
      TransformersEnum.TranslateTitle,
      TransformersEnum.TranslateDescription,
      TransformersEnum.TranslateParameters,
      TransformersEnum.TranslateProperties,
      TransformersEnum.TranslateSizes,
    ],
  },
  {
    id: "mapping",
    title: "Маппинг",
    transformers: [
      TransformersEnum.MappingCategories,
      TransformersEnum.MappingVendorCode,
      TransformersEnum.MappingParameters,
    ],
  },
  {
    id: "cleanup",
    title: "Очистка от иероглифов",
    transformers: [
      TransformersEnum.RemoveHieroglyphs,
      TransformersEnum.RemoveHieroglyphsInVendorCode,
      TransformersEnum.RemoveHieroglyphsInSizeGrid,
    ],
  },
  {
    id: "images",
    title: "Изображения",
    transformers: [
      TransformersEnum.SquareImages,
      TransformersEnum.ImageTransform,
    ],
  },
  {
    id: "normalization",
    title: "Нормализация данных",
    // RoundingPrice переехал в раздел «Ценовые правила» → RoundingOptionForm
    // (поле roundingOption приоритетнее этого трансформера на бэке).
    transformers: [
      TransformersEnum.NormalizeSaleDate,
      TransformersEnum.RuGenders,
    ],
  },
  {
    id: "generation",
    title: "ИИ-генерация",
    transformers: [TransformersEnum.GptTitle, TransformersEnum.GptDescription],
  },
];

export const TRANSLATION_TRANSFORMERS: ReadonlySet<TransformersEnum> = new Set(
  TRANSFORMER_GROUPS.find((g) => g.id === "translation")?.transformers ?? [],
);

export const isTranslationTransformer = (t: TransformersEnum) =>
  TRANSLATION_TRANSFORMERS.has(t);

export const languages: { label: string; value: TranslateLanguage }[] = [
  {
    label: "Албанский",
    value: "SQ",
  },
  {
    label: "Амхарский",
    value: "AM",
  },
  {
    label: "Английский",
    value: "EN",
  },
  {
    label: "Арабский",
    value: "AR",
  },
  {
    label: "Армянский",
    value: "HY",
  },
  {
    label: "Африкаанс",
    value: "AF",
  },
  {
    label: "Баскский",
    value: "EU",
  },
  {
    label: "Башкирский",
    value: "BA",
  },
  {
    label: "Белорусский",
    value: "BE",
  },
  {
    label: "Бенгальский",
    value: "BN",
  },
  {
    label: "Бирманский",
    value: "MY",
  },
  {
    label: "Болгарский",
    value: "BG",
  },
  {
    label: "Боснийский",
    value: "BS",
  },
  {
    label: "Валлийский",
    value: "CY",
  },
  {
    label: "Венгерский",
    value: "HU",
  },
  {
    label: "Вьетнамский",
    value: "VI",
  },
  {
    label: "Гаитянский",
    value: "HT",
  },
  {
    label: "Галисийский",
    value: "GL",
  },
  {
    label: "Горномарийский",
    value: "MRJ",
  },
  {
    label: "Греческий",
    value: "EL",
  },
  {
    label: "Грузинский",
    value: "KA",
  },
  {
    label: "Гуджарати",
    value: "GU",
  },
  {
    label: "Датский",
    value: "DA",
  },
  {
    label: "Зулу",
    value: "ZU",
  },
  {
    label: "Иврит",
    value: "HE",
  },
  {
    label: "Идиш",
    value: "YI",
  },
  {
    label: "Индонезийский",
    value: "ID",
  },
  {
    label: "Ирландский",
    value: "GA",
  },
  {
    label: "Исландский",
    value: "IS",
  },
  {
    label: "Испанский",
    value: "ES",
  },
  {
    label: "Итальянский",
    value: "IT",
  },
  {
    label: "Казахский",
    value: "KK",
  },
  {
    label: "Казахский (латиница)",
    value: "KAZLAT",
  },
  {
    label: "Каннада",
    value: "KN",
  },
  {
    label: "Каталанский",
    value: "CA",
  },
  {
    label: "Киргизский",
    value: "KY",
  },
  {
    label: "Китайский",
    value: "ZH",
  },
  {
    label: "Корейский",
    value: "KO",
  },
  {
    label: "Коса",
    value: "XH",
  },
  {
    label: "Кхмерский",
    value: "KM",
  },
  {
    label: "Лаосский",
    value: "LO",
  },
  {
    label: "Латынь",
    value: "LA",
  },
  {
    label: "Латышский",
    value: "LV",
  },
  {
    label: "Литовский",
    value: "LT",
  },
  {
    label: "Люксембургский",
    value: "LB",
  },
  {
    label: "Македонский",
    value: "MK",
  },
  {
    label: "Малагасийский",
    value: "MG",
  },
  {
    label: "Малайский",
    value: "MS",
  },
  {
    label: "Малаялам",
    value: "ML",
  },
  {
    label: "Мальтийский",
    value: "MT",
  },
  {
    label: "Маори",
    value: "MI",
  },
  {
    label: "Маратхи",
    value: "MR",
  },
  {
    label: "Марийский",
    value: "MHR",
  },
  {
    label: "Монгольский",
    value: "MN",
  },
  {
    label: "Немецкий",
    value: "DE",
  },
  {
    label: "Непальский",
    value: "NE",
  },
  {
    label: "Нидерландский",
    value: "NL",
  },
  {
    label: "Норвежский",
    value: "NO",
  },
  {
    label: "Осетинский",
    value: "OS",
  },
  {
    label: "Панджаби",
    value: "PA",
  },
  {
    label: "Папьяменто",
    value: "PAP",
  },
  {
    label: "Персидский",
    value: "FA",
  },
  {
    label: "Польский",
    value: "PL",
  },
  {
    label: "Португальский",
    value: "PT",
  },
  {
    label: "Румынский",
    value: "RO",
  },
  {
    label: "Русский",
    value: "RU",
  },
  {
    label: "Себуанский",
    value: "CEB",
  },
  {
    label: "Сербский",
    value: "SR",
  },
  {
    label: "Сингальский",
    value: "SI",
  },
  {
    label: "Словацкий",
    value: "SK",
  },
  {
    label: "Словенский",
    value: "SL",
  },
  {
    label: "Суахили",
    value: "SW",
  },
  {
    label: "Сунданский",
    value: "SU",
  },
  {
    label: "Тагальский",
    value: "TL",
  },
  {
    label: "Таджикский",
    value: "TG",
  },
  {
    label: "Тайский",
    value: "TH",
  },
  {
    label: "Тамильский",
    value: "TA",
  },
  {
    label: "Татарский",
    value: "TT",
  },
  {
    label: "Телугу",
    value: "TE",
  },
  {
    label: "Турецкий",
    value: "TR",
  },
  {
    label: "Удмуртский",
    value: "UDM",
  },
  {
    label: "Узбекский",
    value: "UZ",
  },
  {
    label: "Узбекский (кириллица)",
    value: "UZBCYR",
  },
  {
    label: "Украинский",
    value: "UK",
  },
  {
    label: "Урду",
    value: "UR",
  },
  {
    label: "Финский",
    value: "FI",
  },
  {
    label: "Французский",
    value: "FR",
  },
  {
    label: "Хинди",
    value: "HI",
  },
  {
    label: "Хорватский",
    value: "HR",
  },
  {
    label: "Чешский",
    value: "CS",
  },
  {
    label: "Чувашский",
    value: "CV",
  },
  {
    label: "Шведский",
    value: "SV",
  },
  {
    label: "Шотландский (гэльский)",
    value: "GD",
  },
  {
    label: "Эльфийский (синдарин)",
    value: "SJN",
  },
  {
    label: "Эмодзи",
    value: "EMJ",
  },
  {
    label: "Эсперанто",
    value: "EO",
  },
  {
    label: "Эстонский",
    value: "ET",
  },
  {
    label: "Яванский",
    value: "JV",
  },
  {
    label: "Якутский",
    value: "SAH",
  },
  {
    label: "Японский",
    value: "JA",
  },
];

export const COLLAPSE_VARIANT_OPTIONS: {
  label: string;
  value: CreateExportTaskRequestDtoCollapseVariantsByProductIdEnum;
}[] = [
  {
    label: "Минимальная цена",
    value: CreateExportTaskRequestDtoCollapseVariantsByProductIdEnum.Min,
  },
  {
    label: "Максимальная цена",
    value: CreateExportTaskRequestDtoCollapseVariantsByProductIdEnum.Max,
  },
  {
    label: "Средняя цена",
    value: CreateExportTaskRequestDtoCollapseVariantsByProductIdEnum.Avg,
  },
];
